// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

import { Button, Typography } from "antd";
import {
  AndroidOutlined,
  ApiOutlined,
  AppstoreOutlined,
  BellOutlined,
  LockOutlined,
  MessageOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  VideoCameraOutlined,
} from "@ant-design/icons";
import type { ReactNode } from "react";
import { APP_VERSION } from "../version";

// Where the Android APK is hosted. Defaults to /zalmoxis.apk on this origin (drop the file in the
// web server's static root or your reverse proxy); override at build time with VITE_APK_URL to
// point at a CDN or releases page.
const APK_URL: string = import.meta.env.VITE_APK_URL || "/zalmoxis.apk";

type Feature = { icon: ReactNode; title: string; body: string };

const FEATURES: Feature[] = [
  {
    icon: <LockOutlined />,
    title: "End-to-end encrypted",
    body: "Messages, media and calls are sealed to your devices with OpenPGP keys. The homeserver only ever stores ciphertext — never your plaintext, mimetypes or keys.",
  },
  {
    icon: <ApiOutlined />,
    title: "Federated, not walled-in",
    body: "Your server talks to other Zalmoxis servers. Every event is content-addressed and signed with an ed25519 server identity, so you own your data without being an island.",
  },
  {
    icon: <TeamOutlined />,
    title: "IRC-style moderation",
    body: "Operators, voice, bans and a server-run ChanServ — IRC's moderation culture as power levels over a modern, encrypted room-state DAG.",
  },
  {
    icon: <VideoCameraOutlined />,
    title: "Voice & video",
    body: "1:1 and group calls (peer-to-peer mesh or a server-side SFU) over an embedded, self-hosted TURN relay. No coturn, no public STUN, no Google.",
  },
  {
    icon: <AppstoreOutlined />,
    title: "Spaces, DMs & threads",
    body: "Telegram-style forum communities, private direct messages, threaded replies, reactions, edits, polls and pinned messages — all end-to-end encrypted.",
  },
  {
    icon: <BellOutlined />,
    title: "Push without Google",
    body: "An embedded UnifiedPush distributor wakes the app for messages and rings a locked phone for incoming calls — content-free, decentralized, no Firebase required.",
  },
  {
    icon: <SafetyCertificateOutlined />,
    title: "Verifiable key directory",
    body: "A signed, append-only key-transparency log lets clients prove the server can't silently swap in a device key or show different users different keys.",
  },
  {
    icon: <MessageOutlined />,
    title: "A modern IRC client",
    body: "Channels rail, monospace timeline, role sigils and a slash-command composer — the IRC you remember, with encrypted search, media and presence.",
  },
];

// Landing is the public home page shown to logged-out web visitors: what Zalmoxis is, why it
// exists, and a way in. Native app users skip it (they already installed it) and go to Login.
export function Landing({ onEnter }: { readonly onEnter: (mode: "Log in" | "Register") => void }) {
  return (
    <div className="landing">
      <header className="landing-hero">
        <img className="landing-mark" src="/zalmoxis_vault_logo.svg" alt="" aria-hidden />
        <img className="landing-wordmark" src="/logo-wordmark.svg" alt="Zalmoxis" />
        <Typography.Paragraph className="landing-tagline">
          A decentralized, end-to-end-encrypted, federated messaging homeserver — Matrix's room model fused with
          IRC's moderation culture.
        </Typography.Paragraph>
        <div className="landing-cta">
          <Button type="primary" size="large" onClick={() => onEnter("Register")}>
            Create an account
          </Button>
          <Button size="large" className="landing-secondary" onClick={() => onEnter("Log in")}>
            Log in
          </Button>
        </div>
      </header>

      <section className="landing-what">
        <Typography.Title level={3}>What is Zalmoxis?</Typography.Title>
        <Typography.Paragraph type="secondary">
          Zalmoxis is a self-hostable chat homeserver. It keeps the parts of IRC that built communities —
          operators, voice, bans, channel services — and rebuilds them on a Matrix-style room-state DAG that is
          end-to-end encrypted by default and federates between independent servers. The server is a relay for
          signed, encrypted events: it never sees your message contents, and no single company can read, mine, or
          switch off your conversations.
        </Typography.Paragraph>
      </section>

      <section className="landing-features">
        {FEATURES.map((f) => (
          <div className="landing-feature" key={f.title}>
            <span className="landing-feature-icon">{f.icon}</span>
            <Typography.Title level={5} style={{ margin: "0 0 4px" }}>
              {f.title}
            </Typography.Title>
            <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
              {f.body}
            </Typography.Paragraph>
          </div>
        ))}
      </section>

      <section className="landing-download">
        <span className="landing-feature-icon landing-download-icon">
          <AndroidOutlined />
        </span>
        <Typography.Title level={4} style={{ margin: "0 0 4px" }}>
          Get the Android app
        </Typography.Title>
        <Typography.Paragraph type="secondary" style={{ maxWidth: 560, margin: "0 auto 16px" }}>
          Native background push, lock-screen call notifications and a self-hosted distributor — no Google services
          required. This build is <strong>unsigned</strong> for now, so Android will ask you to allow installing from
          your browser; tap through that prompt to install.
        </Typography.Paragraph>
        <Button type="primary" size="large" icon={<AndroidOutlined />} href={APK_URL} download>
          Download APK
        </Button>
      </section>

      <section className="landing-bottom">
        <Typography.Title level={4}>Own your conversations.</Typography.Title>
        <div className="landing-cta">
          <Button type="primary" size="large" onClick={() => onEnter("Register")}>
            Get started
          </Button>
        </div>
      </section>

      <footer className="landing-footer">
        <span>
          <a className="landing-foot-link" href="/terms">
            Terms
          </a>{" "}
          ·{" "}
          <a className="landing-foot-link" href="/privacy">
            Privacy
          </a>{" "}
          · Self-hostable · AGPL-3.0
        </span>
        <span>Zalmoxis {APP_VERSION}</span>
      </footer>
    </div>
  );
}
